import { useState } from "react";
import * as Select from "@radix-ui/react-select";
import { ChevronDown, X, Check } from "lucide-react";
import { cn } from "@/lib/utils";

interface SelectFieldProps {
  value: string;
  onValueChange: (val: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export function SelectField({
  value,
  onValueChange,
  options,
  placeholder = "Select...",
  className,
  disabled = false,
}: SelectFieldProps) {
  const selectedOption = options.find((o) => o.value === value);
  // Remount Radix Select after clear so re-selecting the same option fires onValueChange.
  const [selectKey, setSelectKey] = useState(0);

  const clearSelection = (e: React.MouseEvent | React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onValueChange("");
    setSelectKey((k) => k + 1);
  };

  return (
    <div className={cn("relative w-full max-w-lg", className)}>
      <Select.Root
        key={selectKey}
        value={value || undefined}
        onValueChange={onValueChange}
        disabled={disabled}
      >
        <Select.Trigger
          className={cn(
            "flex items-center justify-between w-full rounded-lg border px-4 py-3 text-sm transition-all bg-white",
            "border-[var(--color-border)] hover:border-[var(--color-brand-primary)]",
            "focus:outline-none focus:ring-2 focus:ring-[var(--color-brand-primary)]/20 focus:border-[var(--color-brand-primary)]",
            "disabled:opacity-50 disabled:cursor-not-allowed",
            value && "border-[var(--color-brand-primary)]"
          )}
          data-testid="schema-select"
        >
          {selectedOption ? (
            <span
              className="inline-flex min-w-0 items-center gap-1.5 rounded-md bg-[var(--color-button-primary-bg)] px-3 py-1 text-sm font-medium text-white"
              title={selectedOption.label}
            >
              <span className="truncate">{selectedOption.label}</span>
            </span>
          ) : (
            <span className="truncate text-[var(--color-text-tertiary)]">{placeholder}</span>
          )}
          <div className="flex shrink-0 items-center gap-1">
            {value && (
              <span
                role="button"
                tabIndex={0}
                onPointerDown={clearSelection}
                onClick={clearSelection}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    clearSelection(e as unknown as React.PointerEvent);
                  }
                }}
                className="p-0.5 rounded hover:bg-[var(--color-bg-tertiary)] cursor-pointer"
              >
                <X className="h-3.5 w-3.5 text-[var(--color-text-secondary)]" />
              </span>
            )}
            <Select.Icon>
              <ChevronDown className="h-4 w-4 text-[var(--color-text-tertiary)]" />
            </Select.Icon>
          </div>
        </Select.Trigger>

        <Select.Portal>
          <Select.Content
            className="z-50 overflow-hidden rounded-lg border border-[var(--color-border)] bg-white shadow-lg animate-in fade-in-0 zoom-in-95"
            position="popper"
            sideOffset={4}
          >
            <Select.Viewport className="p-1">
              {options.map((option) => (
                <Select.Item
                  key={option.value}
                  value={option.value}
                  className="relative flex items-center gap-2 rounded-md px-3 py-2.5 text-sm cursor-pointer select-none outline-none data-[highlighted]:bg-[var(--color-bg-secondary)] data-[highlighted]:text-[var(--color-text-primary)]"
                >
                  <Select.ItemText>{option.label}</Select.ItemText>
                  <Select.ItemIndicator className="ml-auto">
                    <Check className="h-4 w-4 text-[var(--color-brand-primary)]" />
                  </Select.ItemIndicator>
                </Select.Item>
              ))}
            </Select.Viewport>
          </Select.Content>
        </Select.Portal>
      </Select.Root>
    </div>
  );
}
